import type React from "react";
import { type ItineraryLegDTO } from "../../types/dtos/api/itinerary/itinerary.dto";
import { type DynamicIslandShellMotionConfig } from "./animation/dynamicIslandAnimation.types";
import { type DynamicIslandKind, type DynamicIslandPayload } from "./dynamicIsland.types";
import { type DynamicIslandVariantShellOptions } from "./dynamicIslandShell.types";

/**
 * Host capabilities passed to variant content components.
 * Variants use these inside their own interaction handlers (clicks, buttons, scroll, etc.).
 */
export interface DynamicIslandHostContext {
    "updateCamera": ( config: {
        "center":   [number, number];
        "zoom":     number;
        "duration": number;
        "pitch":    number;
        "bearing":  number;
    } ) => void;

    /** Toggles or selects a leg when the itinerary summary island leg chip is clicked. */
    "onItineraryLegClick"?: ( leg: ItineraryLegDTO ) => void;

    /** Whether the map camera is continuously following the selected vehicle. */
    "isFollowingVehicle": boolean;

    /** Toggles vehicle follow mode (center-only camera tracking). */
    "toggleVehicleFollow": () => void;
}

/** Props received by each variant content component. */
export interface DynamicIslandVariantContentProps<P extends DynamicIslandPayload> {
    "payload":     P;
    "isDarkUi":    boolean;
    "hostContext": DynamicIslandHostContext;
}

/** Props for optional per-variant content motion wrappers. */
export interface DynamicIslandContentMotionWrapperProps<P extends DynamicIslandPayload> {
    "animationKey":  string;
    "payload":       P;
    "reducedMotion": boolean;

    /**
     * When true, the shell already ran the kind-switch enter — skip nested enter
     * so content does not appear frozen before a second animation starts.
     */
    "skipEnterAnimation"?: boolean;

    "children": React.ReactNode;
}

/** Per-variant animation registration (keys, overrides, wrappers). */
export interface DynamicIslandVariantAnimationRegistration<P extends DynamicIslandPayload> {

    /**
     * Stable session key for intra-variant transitions.
     * Must ignore high-frequency RT fields (lngLat, delay text, …).
     */
    "getPayloadAnimationKey": ( payload: P ) => string;

    /** Partial shell motion overrides merged over global config. */
    "motionOverrides"?: Partial<DynamicIslandShellMotionConfig>;

    /** Optional wrapper around variant content for crossfade / stagger / micro-motion. */
    "ContentMotionWrapper"?: React.FC<DynamicIslandContentMotionWrapperProps<P>>;

    /**
     * When true, variant wrapper may run a debounced micro-pulse on content delta.
     * Used for RT text updates without changing {@link getPayloadAnimationKey}.
     */
    "shouldAnimateContentDelta"?: ( previous: P, next: P ) => boolean;

    /** Higher value wins the incoming curve on directional kind switch. */
    "kindSwitchPriority"?: number;
}

/** Contract each registered variant must satisfy. */
export interface DynamicIslandVariantRegistration<P extends DynamicIslandPayload = DynamicIslandPayload> {
    "kind":             P["kind"];
    "ContentComponent": React.FC<DynamicIslandVariantContentProps<P>>;

    /** Optional generic shell layers (background, horizontal scroll, …). */
    "shell"?: DynamicIslandVariantShellOptions;

    /** Optional motion registration for shell and content choreography. */
    "animation"?: DynamicIslandVariantAnimationRegistration<P>;
}

const variantRegistry = new Map<DynamicIslandKind, DynamicIslandVariantRegistration>();

/**
 * Registers a Dynamic Island variant for shell rendering.
 *
 * @param registration Variant registration entry.
 */
export function registerDynamicIslandVariant<P extends DynamicIslandPayload>( registration: DynamicIslandVariantRegistration<P> ): void {
    variantRegistry.set( registration.kind, registration as unknown as DynamicIslandVariantRegistration );
}

/**
 * Returns the registration for a payload kind, if any.
 *
 * @param kind Payload discriminator.
 * @returns Registration or undefined.
 */
export function getDynamicIslandVariant( kind: DynamicIslandKind ): DynamicIslandVariantRegistration | undefined {
    return variantRegistry.get( kind );
}
